// ui-kit — H5 原生 DOM + styled-components
import React, { useState } from "react"
import styled, { useTheme } from "~/ui/styled"
import {
  space,
  layout,
  color,
  flexbox,
  border,
  position,
  opacity,
  compose,
  system,
  fontWeight,
  fontFamily,
  textAlign,
  fontStyle,
  letterSpacing,
} from "styled-system"
import { webFontSize, webLineHeight } from "~ui/styled-system-web"
import { useTranslation } from "react-i18next"
import Icon from "~widgets/icon"
import { webSolidButtonStyles } from "~ui/web-controls"
import { shouldForwardProp } from "~/ui/dom-props"
import { flattenStyle } from "~/ui/style"
import { WebText } from "~/ui/web-text"
import { resolveImageSrc, type ImageSrcInput } from "~/ui/image-source"
import { TouchableView } from "~/ui/touchable"
import type { ElButtonProps, ElGradientProps, ElPressHandler, ElStyleProps } from "./el-style-props"

/** El 组件允许 style 透传到 DOM（与 styled-system 短名 props 并存） */
const elShouldForwardProp = (prop: string) => prop === "style" || shouldForwardProp(prop)

const styledDiv = () => styled(TouchableView).withConfig({ shouldForwardProp: elShouldForwardProp })<ElStyleProps>
const styledButton = () => styled.button.withConfig({ shouldForwardProp: elShouldForwardProp })<ElButtonProps>
const styledText = () => styled(WebText).withConfig({ shouldForwardProp: elShouldForwardProp })<ElStyleProps>

const gap = system({
  gap: {
    property: "gap",
    scale: "space",
  },
})

const webTypographyStyle = compose(
  webFontSize,
  webLineHeight,
  fontWeight,
  fontFamily,
  textAlign,
  fontStyle,
  letterSpacing,
)

const systemStyle = compose(
  space,
  layout,
  color,
  flexbox,
  border,
  webTypographyStyle,
  position,
  gap,
)

// 通用块级容器（替代 RN View）
export const Box = styledDiv()`
  box-sizing: border-box;
  display: block;
  min-width: 0;
  min-height: 0;
  ${systemStyle}
`

const BaseFlex = styledDiv()`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  height: ${p=>p.height || "auto"};
  min-width: 0;
  min-height: 0;
  ${systemStyle}
  flex-direction: ${(p: ElStyleProps) => p.flexDirection || "row"};
  align-items: ${(p: ElStyleProps) => p.alignItems || "flex-start"};
  justify-content: ${(p: ElStyleProps) => p.justifyContent || "flex-start"};
`

export const Flex = styled(BaseFlex)``
export const StartFlex = styled(Flex)`
  align-items: ${(p: ElStyleProps) => p.alignItems || "center"};
  justify-content: flex-start;
`
export const CenterFlex = styled(Flex)`
  align-items: ${(p: ElStyleProps) => p.alignItems || "center"};
  justify-content: center;
`
export const AroundFlex = styled(Flex)`
  align-items: ${(p: ElStyleProps) => p.alignItems || "center"};
  justify-content: space-around;
`
export const BetweenFlex = styled(Flex)`

  align-items: ${(p: ElStyleProps) => p.alignItems || "center"};
  justify-content: space-between;
`
export const ColumnFlex = styled(Flex)`
  flex-direction: column;
  align-items: ${(p: ElStyleProps) => p.alignItems || "flex-start"};
`
export const ColumnCenterFlex = styled(Flex)`
  flex-direction: column;
  align-items: ${(p: ElStyleProps) => p.alignItems || "center"};
  justify-content: ${(p: ElStyleProps) => p.justifyContent || "center"};
`

const GridContainer = styledDiv()`
  width: 100%;
  display: grid;
  box-sizing: border-box;
  flex-shrink: 0;
  ${systemStyle}
`
const GridCol = styledDiv()``

type GridProps = ElStyleProps & {
  columns?: number
  gap?: number
  children?: React.ReactNode
}

export const Grid = ({ columns = 1, gap: gapSize = 0, children, ...props }: GridProps) => {
  const normalizedColumns = Number.isFinite(columns) && columns > 0 ? Math.floor(columns) : 1
  return (
    <GridContainer
      {...props}
      style={flattenStyle([
        {
          gridTemplateColumns: `repeat(${normalizedColumns}, minmax(0, 1fr))`,
          gap: `${gapSize}px`,
        },
        props.style as never,
      ])}
    >
      {React.Children.map(children, (child, index) => (
        <GridCol key={index}>
          {child}
        </GridCol>
      ))}
    </GridContainer>
  )
}

export const Text = styledText()`
  ${space}
  ${webTypographyStyle}
  ${color}
`

type TrTextProps = ElStyleProps & { children: string }

export const TrText = ({ children, ...props }: TrTextProps) => {
  const { t } = useTranslation()
  return <Text {...props}>{t(children)}</Text>
}

const ImageRoot = styled.div`
  position: relative;
  overflow: hidden;
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  align-self: stretch;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
`

const StyledImg = styled.img<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  object-fit: cover;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease;
`

const SpinnerOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

const Spinner = styled.div<{ $color: string; $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;
  border: 2px solid rgba(0, 0, 0, 0.08);
  border-top-color: ${({ $color }) => $color};
  border-radius: 50%;
  animation: el-spin 0.8s linear infinite;
`

type ImageProps = ElStyleProps & {
  src?: ImageSrcInput
  style?: React.CSSProperties
  Loading?: React.ComponentType | null
}

export const Image: React.FC<ImageProps> = ({ src, style, Loading = null, ...props }) => {
  const [loaded, setLoaded] = useState(false)
  const theme = useTheme()
  const uri = resolveImageSrc(src)

  return (
    <ImageRoot {...props} style={style}>
      {!loaded && (
        <SpinnerOverlay>
          {Loading ? (
            <Loading />
          ) : (
            <Spinner $color={theme.colors?.secondaryText || "#a2a8af"} $size={24} />
          )}
        </SpinnerOverlay>
      )}
      <StyledImg src={uri} $visible={loaded} onLoad={() => setLoaded(true)} alt="" />
    </ImageRoot>
  )
}

const BaseButton = styledButton()`
  ${space}
  ${layout}
  ${color}
  ${border}
  ${webTypographyStyle}
  ${flexbox}
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: ${(p: ElButtonProps) => p.justifyContent || "center"};
  opacity: ${(p: ElButtonProps) => (p.disabled ? 0.5 : 1)};
  border: none;
  cursor: pointer;
  background: ${(p: ElButtonProps) => p.bg || "transparent"};
  ${webSolidButtonStyles}
`

type ButtonProps = ElButtonProps & {
  loading?: boolean
  /** @deprecated use onClick */
  onPress?: ElPressHandler
  loadingText?: string
  children?: React.ReactNode
}

export const Button = ({
  loading = false,
  disabled = false,
  onClick,
  onPress,
  children,
  loadingText = "保存中",
  ...props
}: ButtonProps) => {
  const theme = useTheme()
  const { t } = useTranslation()
  return (
    <BaseButton
      type="button"
      disabled={disabled || loading}
      onClick={onClick ?? onPress}
      {...props}
    >
      {loading ? (
        <CenterFlex>
          <Spinner $color={theme.colors.textLight} $size={18} />
          {loadingText ? (
            <Text ml="sm" color={theme.colors.textLight} fontSize="sm">
              {t(loadingText)}
            </Text>
          ) : null}
        </CenterFlex>
      ) : (
        children
      )}
    </BaseButton>
  )
}

type IconFontProps = {
  name: string
  color?: string
  size?: number
  [key: string]: unknown
}

export const IconFont = ({ name, color, size = 20, ...props }: IconFontProps) => {
  const theme = useTheme()
  return <Icon name={name} color={theme?.colors?.[color as keyof typeof theme.colors] || color} size={size} {...props} />
}

const FadeIn = styledDiv()`
  animation: el-fade-in 0.3s ease-out;

  @keyframes el-fade-in {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`

type AnimatedFProps = ElStyleProps & {
  isExists?: boolean
  children?: React.ReactNode
}

export const AnimatedF = ({ isExists = true, children, ...props }: AnimatedFProps) => {
  if (!isExists) return null
  return <FadeIn {...props}>{children}</FadeIn>
}

const gradientForwardProp = (prop: string) =>
  shouldForwardProp(prop) && prop !== "colors" && prop !== "start" && prop !== "end"

const gradientBackground = (
  colors: string[],
  start: { x: number; y: number },
  end: { x: number; y: number },
) => {
  const angle = Math.atan2(end.y - start.y, end.x - start.x) * (180 / Math.PI) + 90
  return `linear-gradient(${angle}deg, ${colors.join(", ")})`
}

const GradientWrapper = styled.div.withConfig({ shouldForwardProp: gradientForwardProp })<ElGradientProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  overflow: hidden;
  flex-shrink: 0;
  min-width: 0;
  background-image: ${({ colors = ["#4c9fff", "#026aff"], start = { x: 0, y: 0 }, end = { x: 1, y: 1 } }) =>
    gradientBackground(colors, start, end)};
  ${space}
  ${layout}
  ${border}
  ${flexbox}
  ${opacity}
`

const TouchWrap = styled.button<{ $contentLayout?: "center" | "fill" }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-self: stretch;
  align-items: ${({ $contentLayout = "center" }) => ($contentLayout === "fill" ? "stretch" : "center")};
  justify-content: ${({ $contentLayout = "center" }) => ($contentLayout === "fill" ? "flex-start" : "center")};
  width: 100%;
  min-width: 0;
  min-height: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  box-sizing: border-box;

  &:disabled {
    cursor: not-allowed;
  }
`

const LoadingRow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

type GradientButtonProps = ElGradientProps & {
  loading?: boolean
  disabled?: boolean
  onClick?: ElPressHandler
  /** @deprecated use onClick */
  onPress?: ElPressHandler
  loadingText?: string
  borderRadius?: number | string
  /** center: 图标+文字垂直居中（工具栏按钮）；fill: 铺满宽度（消息卡片） */
  contentLayout?: "center" | "fill"
  children?: React.ReactNode
}

export const GradientButton = ({
  colors = ["#4c9fff", "#026aff"],
  start = { x: 0, y: 0 },
  end = { x: 1, y: 1 },
  loading = false,
  disabled = false,
  onClick,
  onPress,
  children,
  loadingText = "加载中",
  borderRadius = 8,
  contentLayout = "center",
  ...props
}: GradientButtonProps) => {
  const { t } = useTranslation()
  return (
    <GradientWrapper
      colors={colors}
      start={start}
      end={end}
      borderRadius={borderRadius}
      opacity={disabled ? 0.6 : 1}
      {...props}
    >
      <TouchWrap
        type="button"
        $contentLayout={contentLayout}
        onClick={onClick ?? onPress}
        disabled={disabled || loading}
      >
        {loading ? (
          <LoadingRow>
            <Spinner $color="#fff" $size={18} />
            {loadingText ? (
              <Text color="#fff" fontSize="sm">
                {t(loadingText)}
              </Text>
            ) : null}
          </LoadingRow>
        ) : (
          children
        )}
      </TouchWrap>
    </GradientWrapper>
  )
}

type IconButtonProps = ElButtonProps & {
  name: string
  size?: number
  iconSize?: number
  iconColor?: string
  backgroundColor?: string
  gradientColors?: string[] | null
  loading?: boolean
  /** @deprecated use onClick */
  onPress?: ElPressHandler
  style?: React.CSSProperties
}

export const IconButton = ({
  name,
  size = 40,
  iconSize = 20,
  iconColor = "#fff",
  backgroundColor = "transparent",
  gradientColors = null,
  loading = false,
  disabled = false,
  onClick,
  onPress,
  style = {},
  ...props
}: IconButtonProps) => {
  const Wrapper: React.ElementType = gradientColors ? GradientWrapper : BaseButton
  return (
    <Wrapper
      {...(gradientColors
        ? {
            colors: gradientColors,
            start: { x: 0, y: 0 },
            end: { x: 1, y: 1 },
          }
        : { type: "button" as const })}
      {...props}
      onClick={onClick ?? onPress}
      width={size}
      height={size}
      borderRadius={size / 2}
      bg={gradientColors ? undefined : backgroundColor}
      opacity={disabled ? 0.6 : 1}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      style={style}
    >
      {loading ? <Spinner $color="#fff" $size={18} /> : <IconFont name={name} size={iconSize} color={iconColor} />}
    </Wrapper>
  )
}

export const Hr = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  width: 100%;
  margin: 0;
`

export default {
  Box,
  Flex,
  StartFlex,
  CenterFlex,
  BetweenFlex,
  ColumnFlex,
  ColumnCenterFlex,
  Text,
  TrText,
  Image,
  Button,
  IconButton,
  GradientButton,
  IconFont,
  AnimatedF,
  Hr,
}
